// src/store/slices/carteiraSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface Carteira {
  id: string;
  nome: string;
  cpf: string;
  numeroRegistro: string;
  foto?: string;
  qrCode?: string;
  status: 'ativa' | 'pendente' | 'vencida';
  validade: string;
}

interface CarteiraState {
  carteira: Carteira | null;
  loading: boolean;
  error: string | null;
}

const initialState: CarteiraState = {
  carteira: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchCarteira = createAsyncThunk(
  'carteira/fetch',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/carteira/${userId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar carteira');
    }
  }
);

export const updateCarteiraPhoto = createAsyncThunk(
  'carteira/updatePhoto',
  async (
    { carteiraId, photoFile }: { carteiraId: string; photoFile: File },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);

      const response = await axios.put(
        `${API_URL}/carteira/${carteiraId}/photo`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao atualizar foto');
    }
  }
);

// Slice
const carteiraSlice = createSlice({
  name: 'carteira',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCarteira: (state, action: PayloadAction<Carteira>) => {
      state.carteira = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Carteira
    builder.addCase(fetchCarteira.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCarteira.fulfilled, (state, action) => {
      state.loading = false;
      state.carteira = action.payload;
    });
    builder.addCase(fetchCarteira.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Photo
    builder.addCase(updateCarteiraPhoto.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateCarteiraPhoto.fulfilled, (state, action) => {
      state.loading = false;
      if (state.carteira) {
        state.carteira.foto = action.payload.foto;
      }
    });
    builder.addCase(updateCarteiraPhoto.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, setCarteira } = carteiraSlice.actions;
export default carteiraSlice.reducer;
